/* CUSTOMIZATIONS:
- Aggiunti i filtri per categorie
- Aggiunta popup di dettaglio
- Se si tratta di un CT Luogo, al titolo verrà associato o un link esterno altrimenti il link al suo dettaglio, se si è anonimi manderà al link esterno come si comporta l'oggetto Collegamento
*/
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { v4 as uuid } from 'uuid';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import cx from 'classnames';
import { OSMMap } from 'volto-venue';
import { Row, Col, Container, Button } from 'design-react-kit';
import { List } from 'semantic-ui-react';
import { ListingLinkMore } from 'design-comuni-plone-theme/components/ItaliaTheme';
import { UniversalLink } from '@plone/volto/components';
import jwtDecode from 'jwt-decode';
import { isInternalURL, flattenToAppURL } from '@plone/volto/helpers/Url/Url';

const messages = defineMessages({
  default_detail_link: {
    id: 'Vedi',
    defaultMessage: 'Vedi',
  },
  no_markers: {
    id: 'Non ci sono punti da mostrare sulla mappa',
    defaultMessage: 'Non ci sono punti da mostrare sulla mappa',
  },
  filter_by: {
    id: 'map_filtere_by',
    defaultMessage: 'Filtra per',
  },
  mappa: {
    id: 'map',
    defaultMessage: 'Mappa',
  },
  found_n_filtered_items: {
    id: 'map_found_n_filtered_items',
    defaultMessage: 'Trovati {items} in base ai filtri impostati',
  },
});

const MapTemplate = ({
  items,
  isEditMode,
  linkAlign,
  linkTitle,
  linkHref,
  title,
  titleLine,
  show_map_full_width,
  show_map_filters_subjects,
  map_size = 'medium',
  linkmore_id_lighthouse,
}) => {
  const intl = useIntl();
  let history = useHistory();
  const resultsUID = uuid();

  moment.locale(intl.locale);

  const [markers, setMarkers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [filters, setFilters] = useState({ subjects: new Set() });

  const userId = useSelector((state) =>
    state.userSession.token ? jwtDecode(state.userSession.token).sub : null,
  );

  useEffect(() => {
    let points_subjects = new Set();
    let geolocated_items = items.filter(
      (item) =>
        (item.latitude &&
          item.longitude &&
          item.latitude !== 0 &&
          item.longitude !== 0) ||
        (item.geolocation && //for backward compatibility. To remove on next release.
          item.geolocation?.latitude &&
          item.geolocation?.longitude &&
          item.geolocation?.latitude !== 0 &&
          item.geolocation?.longitude !== 0),
    );

    geolocated_items.map((item) => {
      if (item.Subject) {
        item.Subject.forEach((s) => points_subjects.add(s));
      }
    });

    let points = geolocated_items
      .filter((item) => {
        //apply filters
        let show = true;
        if (filters.subjects.size > 0) {
          show = false;
          if (item.Subject?.length > 0) {
            item.Subject.forEach((s) => {
              if (filters.subjects.has(s)) {
                show = true;
              }
            });
          }
        }
        return show;
      })
      .map((item) => {
        return {
          title: item.title,
          ...(item.geolocation
            ? item.geolocation
            : { latitude: item.latitude, longitude: item.longitude }),
          Subject: item.subject,
          onMarkerClick: (e) => {
            if (item['@type'] !== 'Venue') {
              history.push(item['@id']);
            } else if (e.type === 'keydown') {
              e.originalEvent.stopPropagation();
              e.originalEvent.preventDefault();
              let url =
                !userId && item?.getRemoteUrl && item?.getRemoteUrl !== ''
                  ? item.getRemoteUrl
                  : item['@id'];
              if (isInternalURL(url)) {
                history.push(url);
              } else {
                window.location.href = url;
              }
            }
          },
          popupContent: (
            <List bulleted className="list-results">
              {item.title && (
                <>
                  <UniversalLink
                    className="title"
                    href={
                      item['@type'] === 'Venue' && item?.getRemoteUrl && item?.getRemoteUrl !== '' && !userId
                        ? item.getRemoteUrl
                        : flattenToAppURL(item['@id'])
                    }
                  >
                    {item?.title?.split(' - ')[0]?.trim() || ''}
                  </UniversalLink>
                  <br />
                  {item?.location && <span>{item.location}</span>}
                </>
              )}
            </List>
          ),
        };
      });

    setMarkers(points);
    setSubjects(points_subjects);
  }, [items, filters.subjects]);

  const toggleSubject = (s) => {
    let new_subjects = new Set(filters.subjects);
    if (filters.subjects.has(s)) {
      new_subjects.delete(s);
    } else {
      new_subjects.add(s);
    }
    setFilters({ ...filters, subjects: new_subjects });
  };
  return (
    <div
      className="map-template"
      role="group"
      tabIndex={0}
      aria-label={intl.formatMessage(messages.mappa)}
    >
      <Container className="px-4">
        {title && (
          <Row>
            <Col>
              <h2 className={cx('mb-4', { 'title-bottom-line': titleLine })}>
                {title}
              </h2>
            </Col>
          </Row>
        )}

        {show_map_filters_subjects && subjects.size > 0 && (
          <div className="subjects d-flex flex-wrap justify-content-end">
            {[...subjects].map((s) => (
              <Button
                size="xs"
                key={s}
                color="primary"
                outline={!filters.subjects.has(s)}
                onClick={() => {
                  toggleSubject(s);
                }}
                className="m-2"
                aria-label={intl.formatMessage(messages.filter_by) + ' ' + s}
                aria-controls={'map-results-' + resultsUID}
              >
                {s}
              </Button>
            ))}
          </div>
        )}

        {items?.length > 0 && markers?.length > 0 ? (
          <Row
            className={cx('mb-4 map-wrapper', {
              'full-width': show_map_full_width,
              ['size_' + map_size]: map_size,
            })}
            id={'map-results' + resultsUID}
            aria-live="polite"
            tabIndex={0}
          >
            <div className="visually-hidden">
              {intl.formatMessage(messages.found_n_filtered_items, {
                items: markers.length,
              })}
            </div>
            <OSMMap
              markers={markers}
              showTooltip
              showPopup={true}
              mapOptions={{
                scrollWheelZoom: false,
                // tap: false,
                // dragging: false,
              }}
            />
          </Row>
        ) : (
          intl.formatMessage(messages.no_markers)
        )}

        <ListingLinkMore
          title={linkTitle}
          href={linkHref}
          linkAlign={linkAlign}
          className="my-5"
          linkmoreIdLighthouse={linkmore_id_lighthouse}
        />
      </Container>
    </div>
  );
};

MapTemplate.propTypes = {
  items: PropTypes.arrayOf(PropTypes.any).isRequired,
  isEditMode: PropTypes.bool,
  linkTitle: PropTypes.any,
  linkHref: PropTypes.any,
};

export default MapTemplate;
